<script setup>
import { onMounted, onBeforeUnmount, ref, watchEffect, reactive, nextTick } from 'vue'
import { useStore } from '@/store/bucket'
import stripslashes, { replaceS3path, addBR } from '@/Shared/helper.js'
import Modal from '@/Shared/Modal/Modal'
import ButtonRounded from '@/Shared/Buttons/ButtonRounded'
import ButtonFavoriteToggle from '@/Shared/Buttons/ButtonFavoriteToggle'
import Icon from '@/Shared/Icons/Icon'
import useFavorites from '@/Shared/useFavorites.js'

const props = defineProps({
  data: Array,
  category: String,
  titleEn: String,
  titleJa: String,
})

const state = reactive({
  data: [],
  scrollX: 0,
  isFirst: true,
  isModal: false,
})

const favorite = reactive({
  all: [],
})

const itemRef = ref()
const containerRef = ref()

const openModal = (id, category) => {
  useStore().updateModal({ id: id, category: category })
  state.isModal = true
}

const alignCenter = () => {
  const itemW = itemRef.value[0].offsetWidth
  const containerW = containerRef.value.offsetWidth
  const paddingTotal = parseInt(getComputedStyle(containerRef.value).padding) + parseInt(getComputedStyle(itemRef.value[0]).padding)
  const addition = (containerW - itemW) / 2
  if (containerW < itemW * 2 + paddingTotal) {
    containerRef.value.style.paddingLeft = addition + 'px'
    containerRef.value.style.paddingRight = addition + 'px'
  }
}

const scrollToPreviousX = () => {
  const tagetX = sessionStorage.getItem(`${props.category}X`)
  if (!tagetX) return
  if (!location.hash) return
  state.scrollX = parseInt(tagetX)
  containerRef.value.scrollLeft = state.scrollX
}

const scrollToNextItem = (direction) => {
  state.scrollX = containerRef.value.scrollLeft
  const itemW = itemRef.value[0].offsetWidth
  const itemNum = 3
  if (direction === 'forward') state.scrollX += itemW * itemNum
  if (direction === 'backward') state.scrollX -= itemW * itemNum
  containerRef.value.scrollLeft = parseInt(state.scrollX)
}

onMounted(() => {
  const stop = watchEffect(() => {
    if (itemRef.value === undefined) return
    alignCenter()
    scrollToPreviousX()
    state.isFirst = false
    stop()
  })
  if (props.category === 'news') {
    state.data = props.data
  }
  if (props.category === 'brand' || props.category === 'shop') {
    nextTick(() => {
      const { sortFavoriteList } = useFavorites({ favorite: favorite, category: props.category })
      sortFavoriteList()
      state.data = favorite.all
    })
  }
})

onBeforeUnmount(() => {
  sessionStorage.setItem(`${props.category}X`, containerRef.value.scrollLeft)
})

const style = 'h-[300px] w-[250px] bg-white rounded-[20px] overflow-hidden shadow-card relative'
</script>

<template>
  <div class="flex justify-center items-center">
    <div class="absolute w-full left-0">
      <div class="relative my-[30px]">
        <h2 class="page-title !p-0">
          {{ props.titleEn }}
          <small>{{ props.titleJa }}</small>
        </h2>

        <div class="w-full flex justify-center items-center lg:my-[20px]">
          <div class="hidden sm:block absolute z-20 w-full [&>button]:absolute [&>button]:p-[12px] [&>button]:rounded-l-[5px] xl:[&>button]:rounded-[5px] [&>button]:bg-black [&>button]:-translate-y-1/2 [&_svg]:fill-white">
            <button class="left-0 xl:left-[20px] -scale-x-100" @click="scrollToNextItem('backward')">
              <Icon type="arrow" />
            </button>
            <button class="right-0 xl:right-[20px]" @click="scrollToNextItem('forward')">
              <Icon type="arrow" />
            </button>
          </div>

          <ul ref="containerRef" :class="{ 'scroll-smooth': state.isFirst === false }" class="overflow-hidden overflow-x-scroll flex flex-nowrap scrollbar-hidden px-[30px] py-[10px] xs:py-[20px] sm:py-[30px] snap-x snap-mandatory">
            <li v-for="info in state.data" :key="info[props.category + 'ID']" ref="itemRef" class="p-[20px] snap-center snap-always md:snap-normal">
              <template v-if="category === 'news'">
                <a :class="[style, 'block']" class="!h-auto" href="#" @click.prevent="openModal(info.newsID, props.category)">
                  <img :src="replaceS3path(info.newsThumb)" :alt="stripslashes(info.newsTitle)" class="w-full h-[250px] object-cover" width="250" height="250" />
                  <div class="relative mt-[18px] mb-[20px] mx-[20px] h-[36px]">
                    <h3 class="news-title text-[13px] font-semibold leading-[18px]">{{ info.newsTitle }}</h3>
                  </div>
                </a>
              </template>

              <template v-if="category === 'brand'">
                <a :class="[style, 'center-container']" href="#" @click.prevent="openModal(info.brandID, props.category)">
                  <img :src="replaceS3path(info.brandThumb)" :alt="stripslashes(info.brandName)" class="w-full max-w-[150px] max-h-[150px] mb-[20px]" width="150" height="150" />
                  <h3 class="absolute bottom-[30px] font-rift font-semibold text-[20px] tracking-[0.015em]">{{ stripslashes(info.brandName) }}</h3>
                  <ButtonFavoriteToggle category="brand" :slug="info.brandSlug" :disabled="true" class="no-outline absolute top-[20px] right-[20px]" />
                </a>
              </template>

              <template v-if="category === 'shop'">
                <a :class="[style, 'block']" href="#" @click.prevent="openModal(info.shopID, props.category)">
                  <img :src="replaceS3path(info.shopImg1)" :alt="stripslashes(info.shopNameJ)" class="w-full h-[220px] object-cover" width="220" height="220" />
                  <div class="relative m-[20px]">
                    <h3 class="text-[13px] font-semibold leading-[1.4]" v-html="addBR(stripslashes(info.shopNameJ), 'A&Fカントリー ')" />
                    <ButtonFavoriteToggle category="shop" :slug="info.shopSlug" :disabled="true" class="no-outline absolute top-0 right-0" />
                  </div>
                </a>
              </template>
            </li>
          </ul>
        </div>

        <div class="center-container pb-[10px] [&>.btn-rounded]:!w-[200px]">
          <template v-if="category === 'news'"><ButtonRounded text="ニュース一覧" icon="arrow" url="/news" /></template>
          <template v-if="category === 'brand'"><ButtonRounded text="取扱ブランド一覧" icon="arrow" url="/brands" /></template>
          <template v-if="category === 'shop'"><ButtonRounded text="A&amp;F店舗一覧" icon="arrow" url="/stores" /></template>
        </div>
      </div>
    </div>
  </div>

  <teleport to="#app">
    <transition name="fade"><Modal v-if="state.isModal === true" class="modal" @close="state.isModal = false" /></transition>
  </teleport>
</template>
